import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Globe, Warning, ShieldWarning, ArrowsClockwise, Plugs } from "@phosphor-icons/react";
import { api } from "@/lib/api";

function StatCard({ label, value, icon: Icon, tone = "brand", span = 1, hint, to }) {
    const toneClass = {
        brand: "text-[color:var(--dm-brand)]",
        warn: "text-[color:var(--dm-warn)]",
        danger: "text-[color:var(--dm-danger)]",
        success: "text-[color:var(--dm-success)]",
    }[tone];
    const inner = (
        <>
            <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-[color:var(--dm-text-2)] font-semibold">
                    {label}
                </span>
                <Icon size={22} weight="duotone" className={toneClass} />
            </div>
            <div>
                <div
                    className="text-4xl font-extrabold"
                    style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                >
                    {value}
                </div>
                {hint && (
                    <div className="text-xs text-[color:var(--dm-text-2)] mt-1">
                        {hint}
                    </div>
                )}
            </div>
        </>
    );
    const shared = `glass p-6 ${
        span === 2 ? "md:col-span-2" : ""
    } flex flex-col justify-between min-h-[130px]`;
    if (to) {
        return (
            <Link
                to={to}
                data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`${shared} hover:!bg-white/85 hover:scale-[1.01] transition-all cursor-pointer`}
            >
                {inner}
            </Link>
        );
    }
    return (
        <div
            className={shared}
            data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
            {inner}
        </div>
    );
}

export default function DashboardPage() {
    const [stats, setStats] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        api.get("/dashboard/stats")
            .then(({ data }) => setStats(data))
            .catch((e) => setErr(e.message));
    }, []);

    if (err) return <div className="glass p-6 text-sm">{err}</div>;
    if (!stats) return <div className="glass p-6 text-sm">Loading…</div>;

    return (
        <div className="space-y-6" data-testid="dashboard-page">
            <div>
                <h1
                    className="text-3xl font-extrabold"
                    style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                >
                    Overview
                </h1>
                <p className="text-sm text-[color:var(--dm-text-2)] mt-1">
                    Portfolio health across all connected registrar accounts.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <StatCard
                    label="Total Domains"
                    value={stats.total_domains}
                    icon={Globe}
                    span={2}
                    hint="Across all connected accounts"
                    to="/domains"
                />
                <StatCard
                    label="Domain renewals ≤ 30d"
                    value={stats.expiring_30_days}
                    icon={Warning}
                    tone="warn"
                    hint="Grouped by account →"
                    to="/domains?expiring_days=30&sort=registrar_account_name,expires_at"
                />
                <StatCard
                    label="SSL certs ≤ 30d"
                    value={stats.ssl_expiring_30_days}
                    icon={ShieldWarning}
                    tone="danger"
                    hint="Probe SSL first to populate"
                    to="/domains?ssl_expiring_days=30&sort=registrar_account_name,ssl_expires_at"
                />
                <StatCard
                    label="Active Migrations"
                    value={stats.active_migrations}
                    icon={ArrowsClockwise}
                    tone="brand"
                    to="/migrations"
                />
                <StatCard
                    label="Registrar Accounts"
                    value={stats.registrar_accounts}
                    icon={Plugs}
                    tone="success"
                    to="/registrar-accounts"
                />
                <StatCard
                    label="Domain renewals ≤ 60d"
                    value={stats.expiring_60_days}
                    icon={Warning}
                    tone="warn"
                    hint="Grouped by account →"
                    to="/domains?expiring_days=60&sort=registrar_account_name,expires_at"
                />
            </div>

            <div className="glass p-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold">Per-account breakdown</h2>
                    <Link
                        to="/registrar-accounts"
                        className="text-xs text-[color:var(--dm-brand)] font-semibold hover:underline"
                    >
                        Manage accounts →
                    </Link>
                </div>
                {stats.per_account.length === 0 ? (
                    <div className="text-sm text-[color:var(--dm-text-2)] py-6 text-center">
                        No accounts connected yet.{" "}
                        <Link
                            to="/registrar-accounts"
                            className="text-[color:var(--dm-brand)] font-semibold"
                        >
                            Add your first registrar
                        </Link>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {stats.per_account.map((p, i) => (
                            <div
                                key={i}
                                className="glass !rounded-xl p-4 flex flex-col gap-2"
                                data-testid={`account-card-${p.account}`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="mono font-semibold text-sm">{p.account}</span>
                                    <span
                                        className={`pill ${
                                            p.registrar === "godaddy" ? "pill-warn" : "pill-brand"
                                        }`}
                                    >
                                        {p.registrar}
                                    </span>
                                </div>
                                <div
                                    className="text-2xl font-extrabold"
                                    style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                                >
                                    {p.count}
                                </div>
                                <div className="text-xs text-[color:var(--dm-text-2)]">
                                    Next expiry:{" "}
                                    {p.next_expiry
                                        ? new Date(p.next_expiry).toLocaleDateString()
                                        : "—"}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
